import { Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, finalize, take } from 'rxjs/operators';
import { LoadingSpaceType } from 'src/app/core/services/import-export-service-types';
import { ErrorPopupComponent } from 'src/app/modal/components/error-popup/error-popup.component';
import { PalletOverflowRecommendationPopupComponent, PalletOverflowRecommendationResult } from 'src/app/modal/components/pallet-overflow-recommendation-popup/pallet-overflow-recommendation-popup.component';
import { ModalService } from 'src/app/modal/modal.service';
import {
  countLoadingSpacesOfType,
  dismissPalletOverflowPopup,
  isPalletOverflowPopupDismissed,
  leftoverLoadsExceedSpaceDimensions,
  rotatePalletOverflowProjectInstance,
  overflowAddedNotificationKey,
  overflowingLoadingSpaceType,
  overflowingWorkSpaceId,
  palletOverflowSignature,
  shouldOfferPalletCountRecommendation,
} from 'src/app/shared/helpers/pallet-overflow-recommendation.helper';
import { Project } from '../objects/project';
import { Notification } from '../types';
import { NotificationsService } from './notifications.service';
import type { ProjectApiService } from './project-api.service';
import { ProjectService } from './project.service';
import { SharedProjectService } from './shared-project.service';

@Injectable()
export class PalletOverflowRecommendationService {
  private applyingRecommendation = false;
  private modalOpen = false;
  private cancelledSignature: string | null = null;
  private addedNotificationText: string | null = null;
  private addedFromSignature: string | null = null;
  private trackedNotificationTexts: string[] = [];
  private loadAddPending = false;
  /** Overflow signature already shown or already on the project before the latest add. */
  private settledSignature: string | null = null;
  private addLoadUiOpen = false;
  private pendingProject: Project | null = null;
  private isSharedView = false;
  private ignoringNotificationRemoval = false;
  private closeAddLoadUiSubject = new Subject<void>();
  public readonly closeAddLoadUi: Observable<void> = this.closeAddLoadUiSubject.asObservable();

  constructor(
    private modalService: ModalService,
    private injector: Injector,
    private notifications: NotificationsService,
    private translate: TranslateService,
    projectService: ProjectService,
    sharedProjectService: SharedProjectService,
  ) {
    sharedProjectService.isViewingShared.subscribe((isShared) => (this.isSharedView = isShared));
    projectService.project.subscribe((project) => {
      this.dismissAddedNotificationIfReverted(project);
      if (!this.loadAddPending) {
        this.settledSignature = palletOverflowSignature(project);
      }
    });
    this.notifications.notifications.subscribe((items) => this.onNotificationsChanged(items));
  }

  public markLoadAdded(): void {
    this.loadAddPending = true;
  }

  public onProjectReplaced(): void {
    rotatePalletOverflowProjectInstance();
    this.cancelledSignature = null;
    this.settledSignature = null;
    this.loadAddPending = false;
    this.pendingProject = null;
    this.clearTrackedNotifications();
  }

  public setAddLoadUiOpen(open: boolean): void {
    this.addLoadUiOpen = open;
    if (!open) {
      const project = this.pendingProject;
      this.pendingProject = null;
      if (project) {
        this.offerIfNeeded(project);
      }
    }
  }

  public offerIfNeeded(project: Project): void {
    this.dismissAddedNotificationIfReverted(project);
    const signature = palletOverflowSignature(project);
    if (!this.loadAddPending) {
      this.settledSignature = signature;
      return;
    }
    if (this.applyingRecommendation || this.modalOpen || this.isSharedView) {
      return;
    }
    if (this.addLoadUiOpen) {
      this.pendingProject = project;
      if (this.shouldShowOverflowUi(project)) {
        this.closeAddLoadUiSubject.next();
      }
      return;
    }
    if (leftoverLoadsExceedSpaceDimensions(project)) {
      this.loadAddPending = false;
      const alreadySettled =
        signature === this.settledSignature || signature === this.cancelledSignature;
      this.settledSignature = signature;
      if (!alreadySettled) {
        this.openTooLargePopup();
      }
      return;
    }
    if (isPalletOverflowPopupDismissed()) {
      this.loadAddPending = false;
      return;
    }
    if (!shouldOfferPalletCountRecommendation(project)) {
      this.settledSignature = signature;
      return;
    }
    this.loadAddPending = false;
    if (signature === this.settledSignature || signature === this.cancelledSignature) {
      this.settledSignature = signature;
      return;
    }
    this.openPopup(project, signature, overflowingLoadingSpaceType(project));
  }

  private shouldShowOverflowUi(project: Project): boolean {
    if (leftoverLoadsExceedSpaceDimensions(project)) {
      const signature = palletOverflowSignature(project);
      return signature !== this.cancelledSignature && signature !== this.settledSignature;
    }
    if (isPalletOverflowPopupDismissed()) {
      return false;
    }
    if (!shouldOfferPalletCountRecommendation(project)) {
      return false;
    }
    const signature = palletOverflowSignature(project);
    return signature !== this.cancelledSignature && signature !== this.settledSignature;
  }

  private openTooLargePopup(): void {
    this.modalOpen = true;
    this.modalService
      .open(ErrorPopupComponent, {
        headerKey: 'warning',
        messageKey: 'pallet_load_too_large_for_pallet',
        hideHomepageLink: true,
      })
      .pipe(take(1))
      .subscribe(() => {
        this.modalOpen = false;
      });
  }

  private openPopup(
    project: Project,
    signature: string,
    spaceType: LoadingSpaceType | undefined,
  ): void {
    this.modalOpen = true;
    this.modalService
      .open(PalletOverflowRecommendationPopupComponent, { spaceType })
      .pipe(take(1))
      .subscribe((result: PalletOverflowRecommendationResult) => {
        this.modalOpen = false;
        if (result?.doNotShowAgain) {
          dismissPalletOverflowPopup();
        }
        if (result?.confirmed) {
          this.applyRecommendation(project);
          return;
        }
        if (result && !result.confirmed) {
          this.cancelledSignature = signature;
        }
      });
  }

  private applyRecommendation(project: Project): void {
    const spaceType = overflowingLoadingSpaceType(project) ?? LoadingSpaceType.Pallet;
    const workSpaceId = overflowingWorkSpaceId(project);
    const quantityBefore = countLoadingSpacesOfType(project, spaceType);
    if (workSpaceId == null) {
      this.addTrackedNotification('warning', this.translate.instant('recommendations_empty'));
      return;
    }
    this.applyingRecommendation = true;
    this.projectApi()
      .recommendLoadingSpace({ target: { workSpaceId } })
      .pipe(
        take(1),
        catchError(() => of(null as Project | null)),
        finalize(() => {
          this.applyingRecommendation = false;
        }),
      )
      .subscribe((result: Project | null) => {
        if (!result) {
          this.addTrackedNotification('warning', this.translate.instant('recommendations_empty'));
          return;
        }
        const added = Math.max(0, countLoadingSpacesOfType(result, spaceType) - quantityBefore);
        if (added === 0) {
          return;
        }
        const addedText = this.translate.instant(overflowAddedNotificationKey(spaceType), {
          count: added,
        });
        this.addTrackedNotification('good', addedText);
        this.addedFromSignature = palletOverflowSignature(project);
        this.addedNotificationText = addedText;
      });
  }

  private addTrackedNotification(type: 'good' | 'warning', text: string): void {
    this.dismissTrackedNotifications();
    this.trackedNotificationTexts.push(text);
    this.notifications.addNotification(type, text);
  }

  private dismissTrackedNotifications(): void {
    this.ignoringNotificationRemoval = true;
    try {
      for (const text of this.trackedNotificationTexts) {
        this.notifications.removeNotification(text);
        if (text === this.addedNotificationText) {
          this.addedNotificationText = null;
          this.addedFromSignature = null;
        }
      }
    } finally {
      this.ignoringNotificationRemoval = false;
    }
    this.trackedNotificationTexts = [];
  }

  private clearTrackedNotifications(): void {
    this.dismissTrackedNotifications();
    this.addedNotificationText = null;
    this.addedFromSignature = null;
  }

  private dismissAddedNotificationIfReverted(project: Project): void {
    if (!this.addedNotificationText || palletOverflowSignature(project) !== this.addedFromSignature) {
      return;
    }
    const text = this.addedNotificationText;
    this.ignoringNotificationRemoval = true;
    try {
      this.notifications.removeNotification(text);
    } finally {
      this.ignoringNotificationRemoval = false;
    }
    this.trackedNotificationTexts = this.trackedNotificationTexts.filter((item) => item !== text);
    this.addedNotificationText = null;
    this.addedFromSignature = null;
  }

  private onNotificationsChanged(items: Notification[]): void {
    if (this.ignoringNotificationRemoval || !this.addedNotificationText) {
      return;
    }
    if (items.some((item) => item?.text === this.addedNotificationText)) {
      return;
    }
    const closed = this.addedNotificationText;
    this.addedNotificationText = null;
    this.addedFromSignature = null;
    this.trackedNotificationTexts = this.trackedNotificationTexts.filter((item) => item !== closed);
  }

  private projectApi(): ProjectApiService {
    return this.injector.get<ProjectApiService>(
      (require('./project-api.service') as { ProjectApiService: new (...args: unknown[]) => ProjectApiService })
        .ProjectApiService,
    );
  }
}
